/* eslint-disable no-useless-escape */
//#region imports
import { Component, OnInit, HostBinding, inject } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { Auth } from 'aws-amplify';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AppSettingService } from 'src/app/app-setting.service';
import { VMOnlineLoginService } from '../login/vmonline-login.service';
import { LocalRoutes } from 'src/app/local-routes';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../auth/auth.service';
import { CompromisedPasswordsService } from 'src/app/components/authentication/reset-password/compromised-passwords.service';
import { NotificationsService } from '../../controls/notifications/notifications.service';
import {
	ResetPasswordNotification,
	ResetPasswordNotifications,
	CustomNotifications,
} from './reset-password-notifications';
import { getValidationMessage, createCustomErrorMessage } from 'src/app/util/form-component-validation';
import { ToastComponent } from '../../controls/toast/toast.component';

import { InputTextComponent } from '../../controls/input-text/input-text.component';
import { IconComponent } from '../../controls/icons/icon/icon.component';
import { ButtonComponent } from '../../controls/buttons/button/button.component';

//#endregion imports

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    imports: [
    ToastComponent,
    RouterLink,
    ReactiveFormsModule,
    InputTextComponent,
    IconComponent,
    ButtonComponent
],
})
export class ResetPasswordComponent implements OnInit {
	private router = inject(Router);
	private loginService = inject(VMOnlineLoginService);
	private compromisedPasswordsService = inject(CompromisedPasswordsService);
	private appSettingService = inject(AppSettingService);
	private route = inject(ActivatedRoute);
	private authService = inject(AuthService);
	private notificationsService = inject(NotificationsService);

	@HostBinding('className') componentClass: string;

	//#region component declarations
	readonly passwordStrong = 'Your password is strong';
	readonly passwordHint = 'What makes a strong password?';
	readonly invalidPasswordHint = 'Your password is too weak. Please include the following:';
	readonly challengeTOTP = 'SOFTWARE_TOKEN_MFA';
	readonly preferredMFA = 'NOMFA';

	resetPasswordForm = new UntypedFormGroup({
		username: new UntypedFormControl(null, Validators.compose([Validators.required])),
		password: new UntypedFormControl(null, Validators.compose([Validators.required, this.isPasswordStrong()])),
		confirmPassword: new UntypedFormControl(
			null,
			Validators.compose([Validators.required, this.arePasswordsSame()])
		),
		code: new UntypedFormControl(null),
	});
	passwordError: string = null;
	customNotification: CustomNotifications;
	isProcessing = false;
	awsCognitoPoolId: string;
	awsCognitoAppClientId: string;
	isPasswordValid: boolean = this.isStrongPasswordCriteriaSatisfied();
	passwordMinimumCharacters = false;
	passwordMixUpperAndLower = false;
	passwordMixLetterAndNumbers = false;
	passwordSpecialCharacters = false;
	codeRequired = false;
	passwordSubscription$: Subscription;
	confirmPasswordSubscription$: Subscription;

	get heading(): string {
		const password = this.resetPasswordForm?.controls?.password;
		// we need to do a === null check because if user starts typing
		// and then deletes his entry, password is '' empty string and not null
		return password.value === null
			? this.passwordHint
			: password.valid
			? this.passwordStrong
			: this.invalidPasswordHint;
	}
	get confirmPasswordError(): string {
		const confirmPassword = this.resetPasswordForm?.controls?.confirmPassword;
		return confirmPassword.touched && confirmPassword.invalid ? 'Password and confirm password do not match' : null;
	}


	//#endregion component declarations

	//#region constructor
	constructor() {
		this.componentClass = 'main-container';
		this.awsCognitoPoolId = this.appSettingService.appSetting.awsCognitoPoolId;
		this.awsCognitoAppClientId = this.appSettingService.appSetting.awsCognitoAppClientId;
	}
	//#endregion constructor

	ngOnInit(): void {
		// IF RESET IS ENFORCED FROM ADMIN USING USERS SCREEN, WE PASS A ROUTE PARAMETER
		let username = null;

		this.codeRequired = this.route.snapshot.paramMap.get('coderequired') == 'true';
		username = this.route.snapshot.paramMap.get('username');
		if (this.codeRequired) {
			this.resetPasswordForm.controls.code.setValidators(Validators.required);
		}

		// IF THIS IS THE NEW USER CREATED TEMP PASSWORD CHANGE, WE WOULD HAVE A
		// COGNITO USER IN THE SERVICE
		if (this.loginService.cognitoUser) {
			username = this.loginService.cognitoUser.username;
			this.resetPasswordForm.controls.code.clearValidators();
		}

		if (!this.codeRequired && !username) {
			this.router.navigate([LocalRoutes.LOGIN]);
			return;
		}

		this.resetPasswordForm.patchValue({
			username,
		});
	}

	changePassword(): void {
		this.isProcessing = true;

		const newPassword = this.resetPasswordForm.controls.password.value;

		this.compromisedPasswordsService.checkPasswordExposure(newPassword).subscribe((result) => {
			if (!result.passwordChecked) {
				this.notifyUserAndCompleteProcessing(ResetPasswordNotifications.FailedCheck);
			} else if (result.isCompromised) {
				this.resetPasswordForm.controls.password.reset();
				this.resetPasswordForm.controls.confirmPassword.reset();
				this.notifyUserAndCompleteProcessing(ResetPasswordNotifications.ExposedPassword);
			} else {
				this.setNewPassword();
			}
		});
	}

	private notifyUserAndCompleteProcessing(notification: ResetPasswordNotification): void {
		this.notifyUser(notification);
		this.isProcessing = false;
	}

	private notifyUser(notification: ResetPasswordNotification): void {
		this.customNotification = !notification.notificationServiceDetail && notification.customNotificationType;

		this.notificationsService.addNotification({
			severity: notification.notificationServiceSeverity,
			summary: notification.notificationServiceSummary,
			detail: notification.notificationServiceDetail,
			sticky: false,
			key: 'password-reset',
		});
	}

	private setNewPassword(): void {
		// congnito user pool setting
		this.authService.configureAmplify();

		if (!this.codeRequired) {
			Auth.completeNewPassword(
				this.loginService.cognitoUser, // the Cognito User Object
				this.resetPasswordForm.controls.password.value // the new password
			).then(
				() => {
					this.passwordResetSuccessful();
				},
				() => {
					this.notifyUserAndCompleteProcessing(ResetPasswordNotifications.FailureToSet);
				}
			);
		} else {
			Auth.forgotPasswordSubmit(
				this.resetPasswordForm.controls.username.value,
				this.resetPasswordForm.controls.code.value,
				this.resetPasswordForm.controls.password.value
			).then(
				() => {
					this.passwordResetSuccessful();
				},
				(error) => {
					if (error.code === 'CodeMismatchException') {
						const errorNotification: ResetPasswordNotification = {
							...ResetPasswordNotifications.FailureToSet,
							notificationServiceDetail: error.message,
						};

						this.notifyUserAndCompleteProcessing(errorNotification);
					} else if (error.code === 'ExpiredCodeException') {
						const errorNotification: ResetPasswordNotification = {
							...ResetPasswordNotifications.FailureToSet,
							notificationServiceDetail:
								'It looks like you have used that code ' +
								'before. A new verification code will be sent to you, please try again.',
						};

						this.notifyUserAndCompleteProcessing(errorNotification);
						this.resendCode();
					} else {
						this.notifyUserAndCompleteProcessing(ResetPasswordNotifications.FailureToSet);
					}
				}
			);
		}
	}

	passwordResetSuccessful(): void {
		this.notifyUser(ResetPasswordNotifications.SuccessfulReset);

		setTimeout(() => {
			this.isProcessing = false;
			this.router.navigate([LocalRoutes.LOGIN]);
		}, 3000);
	}

	isPasswordStrong(): ValidatorFn {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return (control: AbstractControl): { [key: string]: any } | null => {
			return control.value && this.isStrongPasswordCriteriaSatisfied() ? null : { value: control.value };
		};
	}

	// for confirm password, the only validation we need is to check
	// if it matches the first password. so separating that as a separate
	// function. Earlier we were rechecking the password criteria also.
	arePasswordsSame(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: string } | null => {
			return control.value && control.value === this.resetPasswordForm.controls.password.value
				? null
				: createCustomErrorMessage('Password and confirm password do not match');
		};
	}

	passwordHasMinimumCharacters(): boolean {
		return this.resetPasswordForm.controls.password.value?.length >= 10;
	}

	passwordHasMixUpperAndLower(): boolean {
		const regex = new RegExp(/(?=.*[a-z])(?=.*[A-Z]).*/);
		return regex.test(this.resetPasswordForm.controls.password.value);
	}

	passwordHasMixLetterAndNumbers(): boolean {
		const regex = new RegExp(/(?=.*\d)(?=.*[A-Za-z]).*/);
		return regex.test(this.resetPasswordForm.controls.password.value);
	}

	passwordHasSpecialCharacters(): boolean {
		const password = this.resetPasswordForm.controls.password;
		if (!password.value) {
			return false;
		}

		const regex = new RegExp(/(?=.*["\\\'\^$*.\[\]{}\(\)?\-!@#%&\/,><\:;|_~`]).*/);
		return regex.test(password.value);
	}

	resendCode(): void {
		const userId = this.resetPasswordForm.controls.username.value;
		if (userId) {
			Auth.forgotPassword(userId)
				.then(() => {
					this.notifyUser(ResetPasswordNotifications.SuccessfulCodeResend);
				})
				.catch(() => {
					this.notifyUser(ResetPasswordNotifications.FailedCodeResend);
				});
		}
	}

	// rather than using the big regex, we were already testing
	// each criterion by regex, so checking them all together to see
	// if its a valid password
	isStrongPasswordCriteriaSatisfied(): boolean {
		// we need to assign individual values other wise the return exits on first false
		// and if a criteria lower in the validation is valid, it still shows false
		const hasMinimumChars = this.passwordHasMinimumCharacters();
		const hasMixUpperLower = this.passwordHasMixUpperAndLower();
		const hasMixLetterNum = this.passwordHasMixLetterAndNumbers();
		const hasSpecialChar = this.passwordHasSpecialCharacters();

		return hasMinimumChars && hasMixUpperLower && hasMixLetterNum && hasSpecialChar;
	}

	// for some reason, when one form control (Password) becomes valid
	// it changes the resetPasswordForm to Valid on change event of password
	// then again on any subsequent event, because confirm password is still
	// invalid, it again marks the formGroup as valid. therefore we have to
	// check both formControls manually and also if they are touched or not
	// since for the first time the formControl is shown as valid until its touched
	isButtonDisabled(): boolean {
		const password = this.resetPasswordForm.controls.password;
		const confirmPassword = this.resetPasswordForm.controls.confirmPassword;

		return (
			password.invalid ||
			!(password.valid && password.touched) ||
			confirmPassword.invalid ||
			!(confirmPassword.valid && confirmPassword.touched)
		);
	}

	validateControl(controlName: string, displayName: string): string {
		const control = this.resetPasswordForm.controls[controlName];
		return getValidationMessage(control, displayName);
	}
}
